import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  PencilSimple,
  CheckCircle,
  Copy,
  FloppyDisk,
  X,
  Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import api from "../lib/api";

import { CONCEPTO_LABEL } from "@/constants/recibos";

const CATEGORIAS = [
  "BEBES",
  "BENJAMINES",
  "INFANTILES",
  "JUVENILES",
  "ADULTOS",
];

const emptyTarifa = {
  nombre: "",
  fecha_inicio: new Date().toISOString().slice(0, 10),

  fecha_fin: new Date().toISOString().slice(0, 10),

  activa: false,
  categorias: [],
};

export default function Tarifas() {
  const [tarifas, setTarifas] = useState([]);
  const [modal, setModal] = useState(null);

  const fetchTarifas = async () => {
    try {
      const { data } = await api.get("/tarifas");
      setTarifas(data || []);
    } catch (err) {
      toast.error("No se pudieron cargar las tarifas");
    }
  };

  useEffect(() => {
    fetchTarifas();
  }, []);

  const openCreate = () => {
    setModal({
      isEdit: false,
      data: {
        ...emptyTarifa,
        categorias: [
          {
            categoria: "ADULTOS",
            conceptos: [],
          },
        ],
      },
    });
  };

  const openEdit = (tarifa) => {
    setModal({
      isEdit: true,
      data: structuredClone(tarifa),
    });
  };

  const saveTarifa = async (e) => {
    e.preventDefault();

    try {
      if (modal.isEdit) {
        await api.put(`/tarifas/${modal.data.id}`, modal.data);

        toast.success("Tarifa actualizada");
      } else {
        await api.post("/tarifas", modal.data);

        toast.success("Tarifa creada");
      }

      setModal(null);
      fetchTarifas();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    }
  };

  const activarTarifa = async (tarifa) => {
    try {
      await api.post(`/tarifas/${tarifa.id}/activar`);

      toast.success("Tarifa activada");

      fetchTarifas();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo activar");
    }
  };

  const duplicarTarifa = async (tarifa) => {
    try {
      await api.post(`/tarifas/${tarifa.id}/duplicar`);

      toast.success("Tarifa duplicada");

      fetchTarifas();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo duplicar");
    }
  };

  const eliminarTarifa = async (tarifa) => {
    if (!window.confirm(`¿Eliminar tarifa "${tarifa.nombre}"?`)) {
      return;
    }

    try {
      await api.delete(`/tarifas/${tarifa.id}`);

      toast.success("Tarifa eliminada");

      fetchTarifas();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const updateCategoriaField = (catIdx, key, value) => {
    const next = structuredClone(modal.data.categorias);

    next[catIdx][key] = value;

    setModal({
      ...modal,
      data: {
        ...modal.data,
        categorias: next,
      },
    });
  };

  const updateConcepto = (catIdx, conceptIdx, key, value) => {
    const next = structuredClone(modal.data.categorias);

    next[catIdx].conceptos[conceptIdx][key] = value;

    setModal({
      ...modal,
      data: {
        ...modal.data,
        categorias: next,
      },
    });
  };

  const addConcepto = (catIdx) => {
    const next = structuredClone(modal.data.categorias);

    next[catIdx].conceptos.push({
      tipo: "cuota",
      importe: 0,
      multiplicable: true,
      mes_cobro: null,
    });

    setModal({
      ...modal,
      data: {
        ...modal.data,
        categorias: next,
      },
    });
  };

  const addCategoria = () => {
    const usadas = new Set(modal.data.categorias.map((c) => c.categoria));

    const siguiente = CATEGORIAS.find((c) => !usadas.has(c));

    if (!siguiente) {
      toast.error("Todas las categorías ya existen");
      return;
    }

    setModal({
      ...modal,
      data: {
        ...modal.data,
        categorias: [
          ...modal.data.categorias,
          {
            categoria: siguiente,
            conceptos: [],
          },
        ],
      },
    });
  };

  const removeCategoria = (idx) => {
    setModal({
      ...modal,
      data: {
        ...modal.data,
        categorias: modal.data.categorias.filter((_, i) => i !== idx),
      },
    });
  };

  return (
    <div className="p-6 md:p-10">
      <header className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
            Configuración
          </div>

          <h1 className="font-display text-4xl font-bold text-med-text">
            Tarifas
          </h1>
        </div>

        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-med-primary hover:bg-med-primary-hover text-white rounded-xl px-5 py-2.5 font-semibold"
        >
          <Plus size={18} weight="bold" />
          Nueva tarifa
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {tarifas.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-med-surface border border-med-border rounded-2xl p-6"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-2xl font-bold text-med-text">
                  {t.nombre}
                </h2>

                <div className="mt-2 text-sm text-med-muted">
                  {t.fecha_inicio} → {t.fecha_fin}
                </div>
              </div>

              {t.activa && (
                <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                  ACTIVA
                </span>
              )}
            </div>

            <div className="mt-6 space-y-3">
              {t.categorias.map((cat) => (
                <div
                  key={cat.categoria}
                  className="rounded-xl bg-med-surface2 p-4"
                >
                  <div className="font-semibold text-med-text mb-2">
                    {cat.categoria}
                  </div>

                  <div className="space-y-1 text-sm">
                    {cat.conceptos.map((c, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between"
                      >
                        <span>{CONCEPTO_LABEL[c.tipo] || c.tipo}</span>

                        <span className="font-semibold">
                          {Number(c.importe).toFixed(2)} €
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-med-border">
              {!t.activa && (
                <button
                  onClick={() => activarTarifa(t)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold"
                >
                  <CheckCircle size={16} weight="duotone" />
                  Activar
                </button>
              )}

              <button
                onClick={() => openEdit(t)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-med-border text-med-text hover:border-med-secondary text-sm font-semibold"
              >
                <PencilSimple size={16} weight="duotone" />
                Editar
              </button>

              <button
                onClick={() => duplicarTarifa(t)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-med-border text-med-text hover:border-med-secondary text-sm font-semibold"
              >
                <Copy size={16} weight="duotone" />
                Duplicar
              </button>

              {!t.activa && (
                <button
                  onClick={() => eliminarTarifa(t)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-red-300 text-red-700 hover:bg-red-50 text-sm font-semibold"
                >
                  <Trash size={16} weight="duotone" />
                  Eliminar
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {modal && (
        <div
          className="fixed inset-0 bg-med-text/40 backdrop-blur-sm grid place-items-center z-50 p-4"
          onClick={() => setModal(null)}
        >
          <motion.form
            onSubmit={saveTarifa}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-med-surface rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-4 border-b border-med-border flex items-center justify-between sticky top-0 bg-med-surface z-10">
              <h3 className="font-display text-xl font-bold">
                {modal.isEdit ? "Editar tarifa" : "Nueva tarifa"}
              </h3>

              <button
                type="button"
                onClick={() => setModal(null)}
                className="p-1.5 rounded-lg hover:bg-med-surface2"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Nombre
                  </label>

                  <input
                    value={modal.data.nombre}
                    onChange={(e) =>
                      setModal({
                        ...modal,
                        data: {
                          ...modal.data,
                          nombre: e.target.value,
                        },
                      })
                    }
                    className="w-full rounded-xl bg-med-surface2 border border-transparent px-4 py-2.5 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Fecha inicio
                  </label>

                  <input
                    type="date"
                    value={modal.data.fecha_inicio}
                    onChange={(e) =>
                      setModal({
                        ...modal,
                        data: {
                          ...modal.data,
                          fecha_inicio: e.target.value,
                        },
                      })
                    }
                    className="w-full rounded-xl bg-med-surface2 border border-transparent px-4 py-2.5 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Fecha fin
                  </label>

                  <input
                    type="date"
                    value={modal.data.fecha_fin}
                    onChange={(e) =>
                      setModal({
                        ...modal,
                        data: {
                          ...modal.data,
                          fecha_fin: e.target.value,
                        },
                      })
                    }
                    className="w-full rounded-xl bg-med-surface2 border border-transparent px-4 py-2.5 outline-none"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={addCategoria}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-med-secondary text-white font-semibold"
                >
                  <Plus size={16} weight="bold" />
                  Añadir categoría
                </button>
              </div>

              <div className="space-y-6">
                {modal.data.categorias.map((cat, catIdx) => (
                  <div
                    key={cat.categoria}
                    className="border border-med-border rounded-2xl p-5"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <select
                          value={cat.categoria}
                          onChange={(e) =>
                            updateCategoriaField(
                              catIdx,
                              "categoria",
                              e.target.value,
                            )
                          }
                          className="rounded-xl bg-med-surface2 border border-med-border px-3 py-2 font-semibold"
                        >
                          {CATEGORIAS.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>

                        <button
                          type="button"
                          onClick={() => removeCategoria(catIdx)}
                          className="p-2 rounded-lg text-med-danger hover:bg-med-danger/10"
                        >
                          <X size={16} weight="bold" />
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => addConcepto(catIdx)}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-med-secondary/10 text-med-secondary"
                      >
                        <Plus size={14} weight="bold" />
                        Añadir concepto
                      </button>
                    </div>

                    <div className="space-y-3">
                      {cat.conceptos.map((c, conceptIdx) => (
                        <div
                          key={conceptIdx}
                          className="grid grid-cols-12 gap-2 items-center"
                        >
                          <select
                            value={c.tipo}
                            onChange={(e) =>
                              updateConcepto(
                                catIdx,
                                conceptIdx,
                                "tipo",
                                e.target.value,
                              )
                            }
                            className="col-span-3 rounded-xl bg-med-surface2 px-3 py-2"
                          >
                            <option value="cuota">Cuota</option>
                            <option value="regulacion_cuota">
                              Regulación cuota
                            </option>
                            <option value="gastos">Gastos</option>
                            <option value="loteria">Lotería</option>
                            <option value="rifa_presentacion">
                              Rifa presentación
                            </option>
                            <option value="regalo_ffmm">Regalo FFMM</option>
                            <option value="beneficio_loteria">
                              Beneficio lotería
                            </option>
                            <option value="regulacion_beneficios">
                              Regulación beneficios
                            </option>
                          </select>

                          <input
                            type="number"
                            step="0.01"
                            value={c.importe}
                            onChange={(e) =>
                              updateConcepto(
                                catIdx,
                                conceptIdx,
                                "importe",
                                e.target.value,
                              )
                            }
                            className="col-span-2 rounded-xl bg-med-surface2 px-3 py-2"
                          />

                          <label className="col-span-3 flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={c.multiplicable}
                              onChange={(e) =>
                                updateConcepto(
                                  catIdx,
                                  conceptIdx,
                                  "multiplicable",
                                  e.target.checked,
                                )
                              }
                            />
                            Multiplicable
                          </label>

                          <input
                            type="number"
                            min="1"
                            max="12"
                            value={c.mes_cobro || ""}
                            onChange={(e) =>
                              updateConcepto(
                                catIdx,
                                conceptIdx,
                                "mes_cobro",
                                e.target.value || null,
                              )
                            }
                            placeholder="Mes cobro"
                            className="col-span-2 rounded-xl bg-med-surface2 px-3 py-2"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-med-border flex justify-end gap-3 sticky bottom-0 bg-med-surface">
              <button
                type="button"
                onClick={() => setModal(null)}
                className="px-5 py-2.5 rounded-xl border-2 border-med-border text-med-text"
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-med-primary hover:bg-med-primary-hover text-white font-semibold"
              >
                <FloppyDisk size={18} weight="duotone" />
                Guardar
              </button>
            </div>
          </motion.form>
        </div>
      )}
    </div>
  );
}
